// InputBar — chip-aware text input + submit button.
// Phase 3 Step 2 of the System Consolidation. Extracted from
// DecisionEnginePage.js with no behavioural changes.
//
// Owns NO local state — reads from `useDecisionEngine()` context, dispatches
// callbacks (onInputChange, onInputKeyDown, onSubmit, onRemovePlayer,
// onFocusInput) supplied by the parent. The parent still owns the submit
// flow, lazy-load, and keyboard routing because they touch many other
// pieces of state (history, filteredCommands, filteredAthletes, etc.).
import React from 'react';
import { Send, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';

import { useDecisionEngine } from './state/DecisionEngineContext';

export const InputBar = ({
  onInputChange,
  onInputKeyDown,
  onSubmit,
  onRemovePlayer,
  onFocusInput,
}) => {
  const { state, refs } = useDecisionEngine();
  const { input, loading, selectedPlayers } = state;
  const { inputRef } = refs;

  return (
    <>
      <div
        className="flex-1 flex flex-wrap gap-1.5 items-center border rounded-md px-2 py-1 bg-white dark:bg-gray-900 focus-within:ring-2 focus-within:ring-indigo-500/60 focus-within:border-indigo-400"
        onClick={() => inputRef.current?.focus()}
        data-testid="decision-engine-input-container"
      >
        {selectedPlayers.map((p) => (
          <span
            key={p.id}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-indigo-100 text-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-900"
            data-testid={`player-chip-${p.id}`}
          >
            <span className="max-w-[140px] truncate">{p.full_name}</span>
            <button
              onClick={(e) => { e.stopPropagation(); onRemovePlayer(p.id); }}
              className="hover:text-indigo-900 dark:hover:text-indigo-100"
              aria-label={`Remove ${p.full_name}`}
              data-testid={`player-chip-remove-${p.id}`}
            >
              ✕
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={onInputKeyDown}
          onFocus={onFocusInput}
          placeholder={
            selectedPlayers.length
              ? 'Ask about selected player(s)…'
              : 'Ask a question… @mention a player, or / for commands'
          }
          disabled={loading}
          className="flex-1 min-w-[120px] bg-transparent outline-none text-sm py-1"
          data-testid="decision-engine-input"
        />
      </div>
      <Button
        onClick={onSubmit}
        disabled={loading || !input.trim()}
        data-testid="decision-engine-submit"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
      </Button>
    </>
  );
};
